/**
 * Client mirror of the org rules.
 * @file src/web/shared/domain.ts
 *
 * @llm-rule WHEN: Deciding which controls to RENDER for the signed-in user
 * @llm-rule AVOID: Treating this as a security boundary — it is not one
 * @llm-rule NOTE: The authority is src/api/lib/domain.ts. This copy exists so
 *   the UI doesn't render a Flag button that the server would reject. If the
 *   two ever disagree, the server wins and the user sees an error toast.
 *   Keep the two in sync when the rules change.
 */

export type OrgRole = 'EMPLOYEE' | 'MANAGER' | 'HEAD' | 'COMPANY_ADMIN' | 'SUPER_ADMIN';

export const RANK: Record<OrgRole, number> = {
  EMPLOYEE: 1,
  MANAGER: 2,
  HEAD: 3,
  COMPANY_ADMIN: 4,
  SUPER_ADMIN: 5,
};

export const ROLE_LABEL: Record<OrgRole, string> = {
  EMPLOYEE: 'Employee',
  MANAGER: 'Manager',
  HEAD: 'Head',
  COMPANY_ADMIN: 'Company Admin',
  SUPER_ADMIN: 'Super Admin',
};

/** Ranks that exist inside a customer company. Excludes the platform operator. */
export const COMPANY_ROLES: OrgRole[] = ['EMPLOYEE', 'MANAGER', 'HEAD', 'COMPANY_ADMIN'];

/**
 * Optional designation a Head may carry, granted by the Company Admin.
 * A label only — it confers no rank and gates no permission.
 */
export type HeadDesignation = 'CEO' | 'CTO';

export const HEAD_DESIGNATIONS: HeadDesignation[] = ['CEO', 'CTO'];

/**
 * How attendance applies to a rank.
 *
 * OPTIONAL is retained for stored data and older callers; the live rule now
 * produces only NONE or REQUIRED, because a Head is either on the clock or not.
 */
export type AttendanceMode = 'NONE' | 'OPTIONAL' | 'REQUIRED';

/**
 * How attendance applies to a rank, given the company's delegation map.
 *
 * The Company Admin is never on the clock. Managers and Employees always are.
 * A Head is the only rank the admin decides about: ticking ATTENDANCE for HEAD
 * on the Authorities screen gives them the full clock-in screen and holds them
 * to the working day; leaving it unticked gives them the short summary and no
 * clock-in at all.
 */
export function attendanceMode(
  role: OrgRole,
  capabilities: CapabilityMap = DEFAULT_CAPABILITIES,
): AttendanceMode {
  if (role === 'SUPER_ADMIN' || role === 'COMPANY_ADMIN') return 'NONE';
  if (role === 'HEAD') {
    return capabilities.ATTENDANCE?.includes('HEAD') ? 'REQUIRED' : 'NONE';
  }
  return 'REQUIRED';
}

/** Whether this rank may use the clock-in screen at all. */
export function canClockIn(
  role: OrgRole,
  capabilities: CapabilityMap = DEFAULT_CAPABILITIES,
): boolean {
  return attendanceMode(role, capabilities) !== 'NONE';
}


/** How a person is described in the UI: "Head (CTO)", or just "Manager". */
export function describeRole(role: OrgRole, designation?: HeadDesignation | null): string {
  if (role === 'HEAD' && designation) return `${ROLE_LABEL.HEAD} (${designation})`;
  return ROLE_LABEL[role];
}

/** Only the Company Admin may grant or clear a Head's designation. */
export function canAssignHeadDesignation(role: OrgRole | null | undefined): boolean {
  return role === 'COMPANY_ADMIN';
}

export function isSuperAdmin(role: OrgRole | null | undefined): boolean {
  return role === 'SUPER_ADMIN';
}

export interface ScopedPerson {
  id: string;
  orgRole: OrgRole;
  /** Null only for a SUPER_ADMIN, who belongs to no company. */
  organisationId?: string | null;
  departmentId?: string | null;
  teamId?: string | null;
  /** Who this person reports to. The scope fallback when teams are not set up. */
  managerId?: string | null;
}

export function rankOf(role: OrgRole | undefined | null): number {
  return role ? RANK[role] ?? 0 : 0;
}

/**
 * Who may waive a flag.
 *
 * Heads and Managers only — deliberately NOT the Company Admin. Waiving is a
 * supervisory judgement about someone you actually oversee day to day, not an
 * administrative override. Scope still applies on top: a Manager reaches only
 * the Employees under them, which canSeeRecordsOf decides.
 */
export function canWaiveFlags(role: OrgRole | undefined | null): boolean {
  return role === 'HEAD' || role === 'MANAGER';
}

/**
 * Who can be on the receiving end of a flag.
 *
 * Only Managers and Employees. Heads and the Company Admin are never flagged —
 * neither by a person nor by the engine. Mirrors the server rule of the same
 * name; keep the two in step.
 */
export function canBeFlagged(role: OrgRole | undefined | null): boolean {
  return role === 'MANAGER' || role === 'EMPLOYEE';
}

export function isSupervisor(role: OrgRole | undefined | null): boolean {
  // A platform operator is not a supervisor of anyone — they sit outside every
  // company's hierarchy, so no roster or console applies to them.
  if (isSuperAdmin(role)) return false;
  return rankOf(role) >= RANK.MANAGER;
}

export function attendanceOptional(role: OrgRole | undefined | null): boolean {
  if (isSuperAdmin(role)) return true;
  return rankOf(role) > RANK.MANAGER;
}

/** Mirror of canRemark — decides whether Praise / Flag chips render at all. */
export function canRemark(
  author: ScopedPerson | null,
  subject: ScopedPerson,
  capabilities: CapabilityMap = DEFAULT_CAPABILITIES,
  /** FLAG obeys canBeFlagged; APPRECIATION does not. */
  type: 'APPRECIATION' | 'FLAG' = 'FLAG',
): boolean {
  if (!author) return false;
  if (author.id === subject.id) return false;
  // Tenancy first, matching the server. A platform operator is in no hierarchy,
  // and nobody reaches across companies.
  if (isSuperAdmin(author.orgRole)) return false;
  if (!author.organisationId || author.organisationId !== subject.organisationId) return false;
  if (type === 'FLAG' && !canBeFlagged(subject.orgRole)) return false;
  if (rankOf(author.orgRole) <= rankOf(subject.orgRole)) return false;
  if (!holdsCapability(author.orgRole, 'REMARKS', capabilities)) return false;

  // A direct report is in reach regardless of team structure — without this a
  // company that has people but no teams yet shows a Manager nobody to reach.
  if (subject.managerId && subject.managerId === author.id) return true;

  switch (author.orgRole) {
    // Both reach the whole company: a Head may flag any Manager or Employee.
    case 'COMPANY_ADMIN':
    case 'HEAD':
      return true;
    // A Manager reaches only their own team's Employees.
    case 'MANAGER':
      return Boolean(author.teamId) && author.teamId === subject.teamId;
    default:
      return false;
  }
}

// ---------------------------------------------------------------------------
// Delegated capabilities (mirror of src/api/lib/domain.ts)
// ---------------------------------------------------------------------------

export type Capability =
  | 'REMARKS'
  | 'ATTENDANCE_APPROVAL'
  | 'LEAVE_APPROVAL'
  | 'PAYSLIPS'
  | 'ATTENDANCE'
  | 'TASKS';

/**
 * What the CEO can hand out. ATTENDANCE_APPROVAL is deliberately NOT here:
 * off-site clock-ins are no longer approved by anyone, so offering the
 * authority would be offering a switch wired to nothing. The key stays in the
 * type so companies that already stored it keep loading.
 */
export const CAPABILITIES: Capability[] = [
  'REMARKS',
  'LEAVE_APPROVAL',
  'PAYSLIPS',
  'ATTENDANCE',
  'TASKS',
];

/** The CEO is never listed — they always hold everything. */
export const DELEGATABLE_ROLES: OrgRole[] = ['HEAD', 'MANAGER'];

export const CAPABILITY_LABEL: Record<Capability, string> = {
  REMARKS: 'Raise flags on people below them',
  ATTENDANCE_APPROVAL: 'Approve off-site clock-ins',
  LEAVE_APPROVAL: 'Approve leave applications',
  PAYSLIPS: 'Generate and read the company’s payslips',
  ATTENDANCE: 'Must clock in and out like everyone else',
  TASKS: 'Assign tasks to people below them',
};

export const CAPABILITY_HINT: Record<Capability, string> = {
  REMARKS: 'Raise a flag against someone below them.',
  ATTENDANCE_APPROVAL: 'Accept or reject a clock-in made outside the office geofence.',
  LEAVE_APPROVAL: 'Accept or reject a leave application from someone below them.',
  PAYSLIPS: 'Generate payslips and read every payslip in the company.',
  ATTENDANCE: 'Ticked, this rank gets the full clock-in screen and is held to the working day. Unticked, they see a short summary and no clock-in.',
  TASKS: 'Hand a task with a deadline to someone below them.',
};

export type CapabilityMap = Record<Capability, OrgRole[]>;

export const DEFAULT_CAPABILITIES: CapabilityMap = {
  REMARKS: ['HEAD', 'MANAGER'],
  ATTENDANCE_APPROVAL: ['HEAD', 'MANAGER'],
  LEAVE_APPROVAL: ['HEAD'],
  // Pay is the most sensitive thing here, so it starts delegated to nobody.
  PAYSLIPS: [],
  // Heads are not on the clock unless the admin says so. Managers and
  // Employees always are — this switch does not apply to them.
  ATTENDANCE: [],
  // Assigning work downward is the existing behaviour, so it starts on.
  TASKS: ['HEAD', 'MANAGER'],
};

export function holdsCapability(
  role: OrgRole | undefined | null,
  capability: Capability,
  capabilities: CapabilityMap,
): boolean {
  if (!role || isSuperAdmin(role)) return false;
  if (role === 'COMPANY_ADMIN') return true;
  return capabilities[capability]?.includes(role) ?? false;
}

/**
 * Who may apply for leave.
 *
 * Leave is time away from the working day, so it follows attendance: if your
 * rank is not on the clock, there is nothing to take leave FROM and nobody
 * counting the days. That makes this one rule rather than two lists —
 * a Head can apply exactly when the Company Admin has put Heads on the clock
 * via ATTENDANCE on the Authorities screen.
 *
 * The Company Admin never applies: nobody sits above them to decide it.
 */
export function canApplyForLeave(
  role: OrgRole,
  capabilities: CapabilityMap = DEFAULT_CAPABILITIES,
): boolean {
  if (isSuperAdmin(role)) return false;
  return attendanceMode(role, capabilities) !== 'NONE';
}

// ---------------------------------------------------------------------------
// Presentation helpers
// ---------------------------------------------------------------------------

/** Dot colours. Named tokens, resolved to CSS custom properties in index.css. */
export const DOT_TOKEN = {
  appreciation: 'var(--dot-appreciation)',
  flag: 'var(--dot-flag)',
  offsite: 'var(--dot-offsite)',
  neutral: 'var(--dot-neutral)',
} as const;

export type DotKind = keyof typeof DOT_TOKEN;

/** "9h 14m" from a minute count. */
export function formatDuration(minutes: number | null | undefined): string {
  if (!minutes || minutes < 1) return '—';
  const h = Math.floor(minutes / 60);
  const m = minutes % 60;
  if (!h) return `${m}m`;
  return `${h}h ${String(m).padStart(2, '0')}m`;
}

/** "9:32" in the viewer's locale, from an ISO timestamp. */
export function formatClock(iso: string | null | undefined): string {
  if (!iso) return '—';
  return new Date(iso).toLocaleTimeString(undefined, { hour: '2-digit', minute: '2-digit', hour12: false });
}

/** "2.4 km" / "180 m" — distance phrasing used across the attendance UI. */
export function formatDistance(metres: number | null | undefined): string {
  if (metres === null || metres === undefined) return '—';
  if (metres < 1000) return `${Math.round(metres)} m`;
  return `${(metres / 1000).toFixed(1)} km`;
}

/** Time-of-day greeting used on the clock-in and console headers. */
export function greeting(date = new Date()): string {
  const h = date.getHours();
  if (h < 12) return 'Good morning';
  if (h < 17) return 'Good afternoon';
  return 'Good evening';
}

export const OFFSITE_REASONS = [
  { value: 'CLIENT_VISIT', label: 'Client visit' },
  { value: 'WFH', label: 'WFH' },
  { value: 'TRAVEL', label: 'Travel' },
  { value: 'OTHER', label: 'Other' },
] as const;

export const OFFSITE_REASON_LABEL: Record<string, string> = Object.fromEntries(
  OFFSITE_REASONS.map((r) => [r.value, r.label]),
);

// ---------------------------------------------------------------------------
// Payslips
// ---------------------------------------------------------------------------

/**
 * Who may generate payslips and read the whole company's.
 *
 * Three ways in, checked in this order:
 *   1. The Company Admin, always — they are the one delegating.
 *   2. Any rank the admin has ticked for PAYSLIPS on the Authorities screen.
 *   3. An individual Head granted `canAccessPayslips` directly.
 *
 * Reading your OWN payslip needs none of these — see canReadPayslipOf.
 */
export function canManagePayslips(
  role: OrgRole,
  capabilities: CapabilityMap = DEFAULT_CAPABILITIES,
  individualGrant = false,
): boolean {
  if (isSuperAdmin(role)) return false;
  if (role === 'COMPANY_ADMIN') return true;
  if (capabilities.PAYSLIPS?.includes(role)) return true;
  return role === 'HEAD' && Boolean(individualGrant);
}

/** Only the Company Admin hands out payslip access, and only to a Head. */
export function canGrantPayslipAccess(role: OrgRole): boolean {
  return role === 'COMPANY_ADMIN';
}

/**
 * May `viewer` read `subjectId`'s payslip?
 *
 * Your own, always. Anyone else's, only with manage rights. Deliberately does
 * NOT follow the reporting line — a manager has no business reading their
 * team's pay.
 */
export function canReadPayslipOf(
  viewer: {
    id: string;
    orgRole: OrgRole;
    capabilities?: CapabilityMap;
    canAccessPayslips?: boolean;
  },
  subjectId: string,
): boolean {
  if (viewer.id === subjectId) return true;
  return canManagePayslips(viewer.orgRole, viewer.capabilities, viewer.canAccessPayslips);
}
